import React, { useState, useEffect, Suspense, lazy, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import BookSlotPromoBubble from './components/BookSlotPromoBubble';
import { CMSProvider } from './context/CMSContext';

// Simple Error Boundary
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h1>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#008751] text-white px-6 py-2 rounded-lg"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load pages for code splitting - reduces initial bundle size
const SinglePageLayout = lazy(() => import('./pages/SinglePageLayout'));
const RootsBeforeRoofsPage = lazy(() => import('./pages/RootsBeforeRoofsPage'));
const TreeViewerPage = lazy(() => import('./pages/TreeViewerPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ContactModal = lazy(() => import('./components/ContactModal'));
const BrochureModal = lazy(() => import('./components/BrochureModal'));
const CalibratePage = lazy(() => import('./pages/CalibratePage'));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin"></div>
  </div>
);

function AppContent() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalSource, setContactModalSource] = useState('default');
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);
  const [brochureLeadCode, setBrochureLeadCode] = useState('LEAD001');
  const location = useLocation();
  const navigate = useNavigate();
  const isPrivateTreeRoute = location.pathname.startsWith('/t/');
  const isRootsRoute = location.pathname === '/news/roots-before-roofs';
  const isPrivacyRoute = location.pathname === '/privacy-policy';
  
  const openContactModal = (source = 'default') => {
    setContactModalSource(source);
    setIsContactModalOpen(true);
  };

  // Sync the URL with the brochure modal state so each open is countable in
  // GA4 / Google Ads / GTM (page_view fires on URL change) and so users can
  // share trackable deep-links with UTM params + lead code.
  const BROCHURE_QS = (code) => `?brochure=1&code=${encodeURIComponent(code || 'LEAD001')}&utm_source=website&utm_medium=brochure_button&utm_campaign=lead_capture_brochure`;

  const openBrochureModal = (code = 'LEAD001') => {
    const leadCode = code || 'LEAD001';
    setBrochureLeadCode(leadCode);
    setIsBrochureModalOpen(true);
    // Push a real history entry so back-button closes the popup and GA4 fires
    // a page_view for `?brochure=1` — your CRM can then count brochure opens
    // vs. brochure conversions side-by-side.
    try {
      const target = window.location.pathname + BROCHURE_QS(leadCode);
      if (window.location.search !== BROCHURE_QS(leadCode)) {
        window.history.pushState({ brochure: true, code: leadCode }, '', target);
      }
      // Manually notify GA4 / GTM of the virtual pageview
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: target,
          page_title: 'Download Brochure',
          brochure_code: leadCode,
        });
      }
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'brochure_modal_open',
          page_path: target,
          brochure_code: leadCode,
        });
      }
    } catch (_) { /* no-op */ }
  };

  const closeBrochureModal = () => {
    setIsBrochureModalOpen(false);
    // Strip brochure params from URL when the popup closes so the section-aliased
    // scroll-spy URL (e.g. /, /design) stays clean.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('brochure') || params.has('code') || params.has('utm_campaign')) {
        params.delete('brochure');
        params.delete('code');
        params.delete('utm_source');
        params.delete('utm_medium');
        params.delete('utm_campaign');
        const newSearch = params.toString();
        const clean = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState(null, '', clean);
      }
    } catch (_) { /* no-op */ }
  };

  // Auto-open contact modal if URL has openContact=true parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const treeId = params.get('treeId') || params.get('tree_id');

    if (treeId && location.pathname === '/') {
      const sig = params.get('sig');
      const exp = params.get('exp');
      const token = params.get('token');
      const treeParams = new URLSearchParams();
      if (sig) treeParams.set('sig', sig);
      if (exp) treeParams.set('exp', exp);
      if (token) treeParams.set('token', token);
      const treeSearch = treeParams.toString();
      navigate(`/t/${encodeURIComponent(treeId)}${treeSearch ? `?${treeSearch}` : ''}`, { replace: true });
      return;
    }

    if (params.get('openContact') === 'true') {
      setIsContactModalOpen(true);
      // Track campaign source if present (for QR code tracking)
      const campaignSource = params.get('utm_source') || params.get('source');
      const campaignMedium = params.get('utm_medium') || params.get('medium');
      const campaignName = params.get('utm_campaign') || params.get('campaign');
      const qrId = params.get('qr_id'); // New QR ID parameter from CRM
      
      // Detect if this is a QR code visit
      const isQRVisit = qrId || campaignSource === 'qr_campaign' || campaignSource === 'qr_code' || 
                        campaignMedium === 'qr' || (campaignSource && campaignSource.includes('qr'));
      
      // Store campaign info for lead tracking
      const campaignData = {
        source: campaignSource || 'direct',
        medium: campaignMedium || 'unknown',
        campaign: campaignName || 'unknown',
        is_qr: isQRVisit,
        qr_id: qrId || null,
        qr_source: isQRVisit ? (campaignSource || 'qr_campaign') : null,
        landed_at: new Date().toISOString(),
        landing_page: window.location.pathname
      };
      
      sessionStorage.setItem('memco_campaign', JSON.stringify(campaignData));
      
      // Log for analytics
      if (isQRVisit) {
        console.log('[QR Campaign] QR code visitor detected:', campaignData);
        // Track QR scan event
        if (typeof window !== 'undefined' && window.MemcoAnalytics) {
          window.MemcoAnalytics.track('qr_scan', { qr_id: qrId, qr_source: campaignSource });
        }
      } else if (campaignSource) {
        console.log('[Campaign] Campaign visitor:', campaignData);
      }
      
      // Remove the parameter from URL without reload
      params.delete('openContact');
      const newSearch = params.toString();
      navigate(location.pathname + (newSearch ? '?' + newSearch : ''), { replace: true });
    }

    // Auto-open brochure modal when arriving with ?brochure=1 (mirrors the live
    // memcoskyline.com CTA). Captures &code=LEADxxx for CRM attribution.
    // We DO NOT strip the params — they need to stay in the URL so GA4 / GTM /
    // Google Ads can count each brochure open as a discrete page_view event.
    if (params.get('brochure') === '1' && location.pathname === '/' && !isBrochureModalOpen) {
      const code = params.get('code') || 'LEAD001';
      setBrochureLeadCode(code);
      setIsBrochureModalOpen(true);
      // Manually emit page_view for analytics (CRA SPA, GA4 wouldn't otherwise)
      try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'page_view', {
            page_path: window.location.pathname + window.location.search,
            page_title: 'Download Brochure',
            brochure_code: code,
          });
        }
        if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'brochure_modal_open',
            page_path: window.location.pathname + window.location.search,
            brochure_code: code,
          });
        }
      } catch (_) { /* no-op */ }
    }
  }, [location.search, navigate, location.pathname, isBrochureModalOpen]);

  // Handle browser back-button while brochure modal is open: close popup
  // without leaving the page so URL state stays consistent.
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('brochure') !== '1' && isBrochureModalOpen) {
        setIsBrochureModalOpen(false);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isBrochureModalOpen]);

  return (
    <>
      <ScrollToTop />

      {/* Navbar - Outside other wrappers to stay fixed at top */}
      {!isPrivateTreeRoute && <Navbar onContactClick={openContactModal} />}
      
      {/* Enquire Now Button - tab stack midpoint at 40% of viewport (upper-right area) */}
      {!isPrivateTreeRoute && <div 
        style={{ 
          position: 'fixed',
          right: 0,
          top: '40%',
          transform: 'translateY(calc(-100% - 2px))',
          zIndex: 9998
        }}
      >
        <button
          onClick={() => openContactModal('enquire_now')}
          className="bg-[#008751] text-white py-4 px-2 md:py-8 md:px-3 text-[10px] md:text-xs font-bold tracking-wider hover:bg-[#006e42] transition-colors uppercase whitespace-nowrap rounded-l-xl shadow-lg"
          style={{ writingMode: 'vertical-lr' }}
          data-testid="enquire-now-side-btn"
        >
          ENQUIRE NOW
        </button>
      </div>}

      {/* Book Your Slot - popup on first session visit + persistent sidebar tab below Enquire Now */}
      {!isPrivateTreeRoute && <BookSlotPromoBubble onContactClick={openContactModal} />}

      {/* Tree Viewer Page - Outside scroll wrappers */}
      {isPrivateTreeRoute && (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/t/:treeId" element={<TreeViewerPage />} />
          </Routes>
        </Suspense>
      )}

      {/* Roots Before Roofs Page - Separate page */}
      {isRootsRoute && (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/news/roots-before-roofs" element={<RootsBeforeRoofsPage onContactClick={openContactModal} />} />
          </Routes>
        </Suspense>
      )}

      {/* Privacy Policy - Separate page */}
      {isPrivacyRoute && (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          </Routes>
        </Suspense>
      )}

      {/* Main Single Page Layout */}
      {!isPrivateTreeRoute && !isRootsRoute && !isPrivacyRoute && (
        <div className="min-h-screen bg-white relative">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main single page route — same component is reused for all
                  section-aliased URLs so client-side nav + scroll-spy can keep
                  the URL in sync with the active section without reloading. */}
              <Route path="/" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/design" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/concept" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/amenities" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/location" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/pricing" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/news-events" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />
              <Route path="/contact" element={<SinglePageLayout onContactClick={openContactModal} onBrochureClick={openBrochureModal} />} />

              {/* Older / alternative slugs kept for backward compatibility. */}
              <Route path="/amenities-new" element={<Navigate to="/amenities" replace />} />
              <Route path="/news" element={<Navigate to="/news-events" replace />} />
              
              <Route path="/studio-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/studio-apartments-south-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/apartments-near-singasandra-metro" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-hosa-road-metro" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-begur-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/flats-in-singasandra" element={<Navigate to="/" replace />} />
              <Route path="/flats-in-begur-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/apartments-near-electronic-city" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-hsr-layout" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-koramangala" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-btm-layout" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-silk-board" element={<Navigate to="/#location" replace />} />
              <Route path="/flats-near-bangalore-metro-yellow-line" element={<Navigate to="/#location" replace />} />
              
              {/* SEO-friendly redirect routes - IT Park proximity */}
              <Route path="/apartments-near-it-parks-bangalore" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-amr-tech-park" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-infosys-electronic-city" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-wipro-electronic-city" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-rmz-ecospace" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-embassy-tech-village" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-prestige-tech-park" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-near-manyata-tech-park" element={<Navigate to="/#location" replace />} />
              
              {/* SEO-friendly redirect routes - Property type */}
              <Route path="/1ldk-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/1rk-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/high-rise-apartments-bangalore" element={<Navigate to="/#concept" replace />} />
              <Route path="/premium-studio-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/affordable-studio-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/apartments-for-young-professionals-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/bachelor-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/gated-community-apartments-bangalore" element={<Navigate to="/" replace />} />
              
              {/* SEO-friendly redirect routes - Features & USP */}
              <Route path="/eco-friendly-apartments-bangalore" element={<Navigate to="/#concept" replace />} />
              <Route path="/sustainable-living-apartments-bangalore" element={<Navigate to="/#concept" replace />} />
              <Route path="/apartments-with-coworking-space-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/apartments-with-rooftop-pool-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/apartments-with-podcast-studio" element={<Navigate to="/#amenities" replace />} />
              <Route path="/apartments-with-gaming-room-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/apartments-with-gym-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/metro-connected-apartments-bangalore" element={<Navigate to="/#location" replace />} />
              <Route path="/apartments-with-best-carpet-area-bangalore" element={<Navigate to="/#concept" replace />} />
              
              {/* SEO-friendly redirect routes - Buyer intent */}
              <Route path="/buy-studio-apartment-bangalore" element={<Navigate to="/?openContact=true" replace />} />
              <Route path="/best-studio-apartments-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/new-apartments-bangalore-2026" element={<Navigate to="/" replace />} />
              <Route path="/investment-property-bangalore" element={<Navigate to="/" replace />} />
              <Route path="/nri-investment-property-bangalore" element={<Navigate to="/?openContact=true" replace />} />
              <Route path="/first-home-bangalore" element={<Navigate to="/" replace />} />
              
              {/* SEO-friendly redirect routes - Lifestyle */}
              <Route path="/work-from-home-friendly-apartments-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/community-living-apartments-bangalore" element={<Navigate to="/#amenities" replace />} />
              <Route path="/smart-studio-apartments-bangalore" element={<Navigate to="/" replace />} />
              
              {/* Catch-all: redirect any unknown route to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <ContactModal 
              isOpen={isContactModalOpen} 
              onClose={() => setIsContactModalOpen(false)}
              source={contactModalSource}
            />
            <BrochureModal
              isOpen={isBrochureModalOpen}
              onClose={closeBrochureModal}
              leadCode={brochureLeadCode}
            />
          </Suspense>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <CMSProvider>
        <Router>
          <AppContent />
        </Router>
      </CMSProvider>
    </ErrorBoundary>
  );
}

export default App;
